// src/components/resources/glossary/NumberedListSection.tsx

/**
 * NumberedListSection — the single list-style section primitive shared by
 * BOTH glossary render paths (the SSR `GlossaryTermServerContent` and the
 * client `TabContent`). It renders a heading + a list of short strings, each
 * with a colored badge.
 *
 * It is **server-safe** (no `"use client"`, no hooks, no browser APIs), so the
 * server component imports it directly and the client component composes it
 * inside its tabbed UI without forcing either path to change its look.
 *
 * The progressive-enhancement divergence is preserved via `variant`:
 * - `flat` — the SSR/crawlable presentation: a plain `<section>` with an `<h2>`
 *   and static list items (sanitized via `safeString`). Used by the server path.
 * - `card` — the client/tabbed presentation: a bordered card with an `<h3>` and
 *   `group-hover` affordances. Used by `TabContent`.
 *
 * Only the axes that legitimately differ are parameterized (`variant`, `color`,
 * and — for `flat` — the `badge` shape + `fallback`). Everything else (the
 * per-section colour classes) lives in the K3 maps below so the two paths share
 * one source of truth for colour instead of re-hardcoding it at each call site.
 */

import { safeString } from '~/lib/utils/formatters'

export type SectionColor = 'blue' | 'green' | 'purple' | 'emerald' | 'indigo' | 'rose'

/** Badge rendered next to each `flat` item: a number (1, 2, 3…), a check, or a small dot. */
export type ListBadge = 'number' | 'check' | 'dot'

interface NumberedListSectionProps {
  heading: string
  items: string[]
  color: SectionColor
  /**
   * `flat` = SSR presentation (plain `<section>` + `<h2>`, static, sanitized).
   * `card` = client presentation (bordered card + `<h3>`, hover affordances).
   */
  variant: 'flat' | 'card'
  /** Badge shape for the `flat` variant. `card` always renders a numbered badge. Defaults to `number`. */
  badge?: ListBadge
  /** Per-item fallback for empty/invalid entries (used by the sanitized `flat` variant). */
  fallback?: string
}

/**
 * K3 — single source of truth for per-section badge colours.
 *
 * `FLAT_BADGE` is the muted SSR palette (`-100`/`-900/20` fill, `-600`/`-400`
 * text). `CARD_BADGE` is the client palette (`-50` fill with `group-hover`
 * darkening). Keeping both here means a colour tweak happens in one place
 * rather than across two files.
 */
const FLAT_BADGE: Record<SectionColor, { bg: string; text: string }> = {
  blue: { bg: 'bg-blue-100 dark:bg-blue-900/20', text: 'text-blue-600 dark:text-blue-400' },
  green: { bg: 'bg-green-100 dark:bg-green-900/20', text: 'text-green-600 dark:text-green-400' },
  purple: { bg: 'bg-purple-100 dark:bg-purple-900/20', text: 'text-purple-600 dark:text-purple-400' },
  emerald: { bg: 'bg-emerald-100 dark:bg-emerald-900/20', text: 'text-emerald-600 dark:text-emerald-400' },
  indigo: { bg: 'bg-indigo-100 dark:bg-indigo-900/20', text: 'text-indigo-600 dark:text-indigo-400' },
  rose: { bg: 'bg-rose-100 dark:bg-rose-900/20', text: 'text-rose-600 dark:text-rose-400' },
}

const CARD_BADGE: Record<SectionColor, string> = {
  blue: 'bg-blue-50 text-blue-600 group-hover:bg-blue-100 group-hover:text-blue-700 dark:bg-blue-900/20 dark:text-blue-400 dark:group-hover:bg-blue-900/30 dark:group-hover:text-blue-300',
  green:
    'bg-green-50 text-green-600 group-hover:bg-green-100 group-hover:text-green-700 dark:bg-green-900/20 dark:text-green-400 dark:group-hover:bg-green-900/30 dark:group-hover:text-green-300',
  purple:
    'bg-purple-50 text-purple-600 group-hover:bg-purple-100 group-hover:text-purple-700 dark:bg-purple-900/20 dark:text-purple-400 dark:group-hover:bg-purple-900/30 dark:group-hover:text-purple-300',
  emerald:
    'bg-emerald-50 text-emerald-600 group-hover:bg-emerald-100 group-hover:text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400 dark:group-hover:bg-emerald-900/30 dark:group-hover:text-emerald-300',
  indigo:
    'bg-indigo-50 text-indigo-600 group-hover:bg-indigo-100 group-hover:text-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-400 dark:group-hover:bg-indigo-900/30 dark:group-hover:text-indigo-300',
  rose: 'bg-rose-50 text-rose-600 group-hover:bg-rose-100 group-hover:text-rose-700 dark:bg-rose-900/20 dark:text-rose-400 dark:group-hover:bg-rose-900/30 dark:group-hover:text-rose-300',
}

/** SSR/crawlable presentation: plain section, static list, sanitized text. */
function FlatList({
  heading,
  items,
  color,
  badge,
  fallback,
}: {
  heading: string
  items: string[]
  color: SectionColor
  badge: ListBadge
  fallback?: string
}): JSX.Element {
  const palette = FLAT_BADGE[color]
  return (
    <section className="mb-8">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{heading}</h2>
      <ul className={badge === 'dot' ? 'space-y-2' : 'space-y-3'}>
        {items.map((item, index) => (
          <li key={index} className="flex items-start">
            {badge === 'dot' ? (
              <span
                className={`flex-shrink-0 w-2 h-2 ${palette.bg} rounded-full mr-3 mt-2`}
              ></span>
            ) : (
              <span
                className={`flex-shrink-0 w-6 h-6 ${palette.bg} rounded-full flex items-center justify-center ${palette.text} text-sm font-medium mr-3 mt-0.5`}
              >
                {badge === 'check' ? '✓' : index + 1}
              </span>
            )}
            <span className="text-gray-700 dark:text-gray-300">
              {safeString(item, { fallback })}
            </span>
          </li>
        ))}
      </ul>
    </section>
  )
}

/** Client/tabbed presentation: bordered card with hover affordances, numbered badges. */
function CardList({
  heading,
  items,
  color,
}: {
  heading: string
  items: string[]
  color: SectionColor
}): JSX.Element {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-900/50">
      <h3 className="text-xl font-semibold mb-4">{heading}</h3>
      <ul className="space-y-4">
        {items.map((item, index) => (
          <li
            key={index}
            className="flex items-start gap-3 group transition-transform duration-200 motion-safe:hover:scale-[1.01]"
          >
            <div
              className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-sm font-medium ${CARD_BADGE[color]} transition-colors`}
            >
              {index + 1}
            </div>
            <p className="text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-gray-200 transition-colors">
              {item}
            </p>
          </li>
        ))}
      </ul>
    </div>
  )
}

/**
 * Renders a list-style glossary section (Key Points, Best Practices, etc.).
 * Returns `null` for an empty list so call sites can render it unconditionally.
 */
export function NumberedListSection({
  heading,
  items,
  color,
  variant,
  badge = 'number',
  fallback,
}: NumberedListSectionProps): JSX.Element | null {
  if (!items?.length) return null
  return variant === 'flat' ? (
    <FlatList heading={heading} items={items} color={color} badge={badge} fallback={fallback} />
  ) : (
    <CardList heading={heading} items={items} color={color} />
  )
}
